<script setup lang="ts">
import { HgCollapsible, HgSupportedLanguage } from '@smg-real-estate/domus';
import type { GeoLocation, GeoLocationType } from '@smg-real-estate/geo-libs';
import { storeToRefs } from 'pinia';
import { computed, onServerPrefetch, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import type { RouteLocationRaw } from 'vue-router';

import { useFeatureToggleQuery } from '@/composables/useFeatureToggle';
import { isServer } from '@/helpers/Platform/platform';
import { getHomeRouteName } from '@/helpers/Url/Url';
import { useLocale } from '@/router/routeMeta';
import { useAgencyProfilesStore } from '@/stores/agencyProfiles';
import { renderGeoLocationName, useGeoStore } from '@/stores/geo';

const SWISS_COUNTRY_LOCATION_ID = 'geo-country-switzerland';
const EXCLUDED_CHILD_LOCATION_TYPES = new Set<GeoLocationType>([
  'cityregion',
  'citydistrict',
  'zip',
  'ziparea',
]);

const CHILD_TITLE_TYPES = new Set<GeoLocationType>([
  'country',
  'canton',
  'district',
  'region',
  'municipality',
  'place',
]);

interface SeoLink {
  id: string;
  name: string;
  route: RouteLocationRaw;
}

const { t } = useI18n();
const language = useLocale();

const agencyProfilesStore = useAgencyProfilesStore();
const geoStore = useGeoStore();
const { searchedLocations } = storeToRefs(agencyProfilesStore);
const featureToggleQuery = useFeatureToggleQuery();
const hasFeatureToggleQuery = computed(() => Object.keys(featureToggleQuery.value).length > 0);

const isSeoChildLocation = (location: GeoLocation | undefined): location is GeoLocation =>
  !!location && !EXCLUDED_CHILD_LOCATION_TYPES.has(location.type);

const primaryLocationId = computed(() => {
  const primaryLocation = searchedLocations.value?.[0];
  return primaryLocation?.id ?? SWISS_COUNTRY_LOCATION_ID;
});

const parentLocation = computed(() => geoStore.getGeoLocationById(primaryLocationId.value));

const childLocations = computed(() => {
  const childIds = parentLocation.value?.children ?? [];

  return childIds.map((childId) => geoStore.getGeoLocationById(childId)).filter(isSeoChildLocation);
});

const getLocalizedUrlName = (location: GeoLocation): string =>
  location.urlNames[language.value] ?? location.urlNames[HgSupportedLanguage.DE];

const seoLinks = computed<SeoLink[]>(() =>
  childLocations.value.map((location) => ({
    id: location.newId ?? location.id,
    name: renderGeoLocationName(location, language.value),
    route: {
      name: getHomeRouteName(language.value),
      params: {
        location: getLocalizedUrlName(location),
      },
      // Keep the links free of query params unless a feature toggle is active.
      ...(hasFeatureToggleQuery.value ? { query: featureToggleQuery.value } : {}),
    },
  })),
);

const childTitleKey = computed(() => {
  const childType = childLocations.value[0]?.type;

  return childType && CHILD_TITLE_TYPES.has(childType)
    ? `agencyDirectorySeoLinks.childTitle.${childType}`
    : 'agencyDirectorySeoLinks.childTitle.default';
});

const collapsibleTitle = computed(() => {
  const location = parentLocation.value;

  if (!location) {
    return '';
  }

  return t(childTitleKey.value, {
    location: renderGeoLocationName(location, language.value),
  });
});

const resolveSeoLocations = async () => {
  await geoStore.resolveLocationsById([primaryLocationId.value]);

  const location = geoStore.getGeoLocationById(primaryLocationId.value);
  const childIds = location?.children ?? [];

  if (childIds.length === 0) {
    return;
  }

  await geoStore.resolveLocationsById(childIds);
};

if (isServer()) {
  onServerPrefetch(resolveSeoLocations);
} else {
  watch(primaryLocationId, resolveSeoLocations, { immediate: true });
}
</script>

<template>
  <article
    v-if="seoLinks.length > 0"
    :class="$style.container"
    data-test="agency-directory-seo-links"
  >
    <h2 :class="$style.title">{{ t('agencyDirectorySeoLinks.title') }}</h2>

    <HgCollapsible
      :doExpandTransition="false"
      :triggerCssClass="$style.listTrigger"
      :triggerCssClassFolded="$style.listTriggerFolded"
      :bodyCssClassUnfolded="$style.listBodyUnfolded"
    >
      <template #header>
        <div :class="$style.listTitle">{{ collapsibleTitle }}</div>
      </template>

      <ul :class="$style.list">
        <li v-for="link in seoLinks" :key="link.id" :class="$style.listItem">
          <router-link :to="link.route">
            {{ link.name }}
          </router-link>
        </li>
      </ul>
    </HgCollapsible>
  </article>
</template>

<style module lang="scss">
@use '@/assets/variables' as *;

.container {
  margin-top: $space-250;
  margin-bottom: $space-200;
}

.title {
  margin: 0 0 $space-100;

  @include heading-2;
}

.listTrigger {
  position: relative;
}

.listTriggerFolded::after {
  position: absolute;
  top: 100%;
  width: 100%;
  content: '';
  border-bottom: 1px solid $color-grey-50;
}

.listBodyUnfolded {
  padding: $space-50 0;
  border-bottom: 1px solid $color-grey-70;
}

.listTitle {
  padding: $space-75 0;
  color: $color-grey-100;

  @include heading-4;
}

.list {
  display: grid;
  grid-template-columns: 1fr;
  row-gap: $space-50;
  padding: 0 0 $space-50;
  margin: 0;
  list-style-type: none;

  @include body-2;

  @media (min-width: $viewport-md) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    column-gap: $space-200;
  }
}

.listItem {
  position: relative;
  padding-left: $space-50;
}

.listItem::before {
  position: absolute;
  left: 0;
  content: '–';
}
</style>
